import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Bell, MessageSquare, User, LogOut, Search, Home, Tag, Package, Building, FileText, Bot, Briefcase, Settings, Headset, BookOpen, Wallet, Wand2 } from 'lucide-react';
import authStore from '../../store/authStore';
import SupportPanel from '../SupportPanel';
import api from '../../utils/api';

const Navbar = () => {
  const { user, logout } = authStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const [walletBalance, setWalletBalance] = useState(null);

  const hiddenPaths = ['/login', '/register', '/verify-email', '/admin'];
  const isHidden = hiddenPaths.some(p => location.pathname.startsWith(p));
  const isLandingNoUser = location.pathname === '/' && !user;

  // Fetch unread support count
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const res = await api.get('/api/support/unread-count');
        setSupportUnread(res.data?.unread_count || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch wallet balance
  useEffect(() => {
    if (!user) return;
    const fetchBalance = async () => {
      try {
        const res = await api.get('/api/wallet/balance');
        setWalletBalance(res.data?.balance ?? null);
      } catch {}
    };
    fetchBalance();
    const interval = setInterval(fetchBalance, 60000);
    return () => clearInterval(interval);
  }, [user]);

  if (isHidden || isLandingNoUser) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/search', label: 'Cerca', icon: Search },
    { path: '/marketplace', label: 'Marketplace', icon: Package },
    { path: '/properties', label: 'Immobili', icon: Building },
    { path: '/requests', label: 'Richieste', icon: FileText },
    { path: '/rivista', label: 'Rivista', icon: BookOpen },
    { path: '/promotions', label: 'Promo', icon: Tag },
  ];

  return (
    <header className="bg-[#1A1A2E] text-white sticky top-0 z-50 shadow-lg" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="navbar-logo">
          <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-white/10 p-1">
            <img
              src="https://customer-assets.emergentagent.com/job_makehome-frontend/artifacts/pnxu5fqe_6.png"
              alt="MakeHome 360"
              className="w-full h-full object-contain"
            />
          </div>
        </Link>

        {/* Desktop nav - hidden when sidebar is visible */}
        <nav className="hidden md:flex lg:hidden items-center gap-1">
          {navLinks.map(link => {
            const isActive = link.path === '/' ? location.pathname === '/' : location.pathname.startsWith(link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? 'bg-white/10 text-[#10B981]' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/ai/redesign" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 hover:from-violet-500/30 hover:to-fuchsia-500/30 transition" data-testid="nav-ai-studio">
                <Wand2 className="w-4 h-4 text-fuchsia-300" />
                <span className="text-[11px] font-bold text-fuchsia-200 hidden sm:inline">AI Studio</span>
              </Link>
              <Link to="/wallet" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 transition" data-testid="nav-wallet">
                <Wallet className="w-4 h-4 text-blue-400" />
                <span className="text-[11px] font-bold text-blue-300">
                  {walletBalance !== null ? `€${walletBalance.toFixed(2).replace('.', ',')}` : 'Wallet'}
                </span>
              </Link>
              <Link to="/notifications" className="relative p-2 rounded-lg hover:bg-white/10 transition hidden md:flex" data-testid="nav-notifications">
                <Bell className="w-5 h-5 text-[#F59E0B]" />
              </Link>
              <Link to="/messages" className="p-2 rounded-lg hover:bg-white/10 transition hidden md:flex" data-testid="nav-messages">
                <MessageSquare className="w-5 h-5" />
              </Link>
              <button onClick={() => setSupportOpen(true)} className="relative p-2 rounded-lg hover:bg-white/10 transition hidden md:flex" data-testid="nav-support">
                <Headset className="w-5 h-5 text-[#10B981]" />
                {supportUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold min-w-[16px] h-4 rounded-full flex items-center justify-center px-1">
                    {supportUnread}
                  </span>
                )}
              </button>
              <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/10 transition"
                  data-testid="nav-profile-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#10B981] flex items-center justify-center text-sm font-bold">
                      {user.name?.charAt(0)}
                    </div>
                  )}
                  <span className="hidden md:block text-sm font-medium max-w-[100px] truncate">{user.name}</span>
                </button>
                {profileOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 text-gray-800">
                      <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition" onClick={() => setProfileOpen(false)}>
                        <User className="w-4 h-4 text-gray-500" /> <span className="text-sm font-medium">Profilo</span>
                      </Link>
                      <Link to="/wallet" className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition" onClick={() => setProfileOpen(false)} data-testid="dropdown-wallet">
                        <Wallet className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Wallet</span>
                        {walletBalance !== null && <span className="ml-auto text-[11px] font-bold text-blue-600">€{walletBalance.toFixed(2).replace('.', ',')}</span>}
                      </Link>
                      {user.is_professional && (
                        <Link to="/crm" className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition" onClick={() => setProfileOpen(false)}>
                          <Briefcase className="w-4 h-4 text-gray-500" /> <span className="text-sm font-medium">CRM</span>
                        </Link>
                      )}
                      <Link to="/ai-chat" className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition" onClick={() => setProfileOpen(false)}>
                        <Bot className="w-4 h-4 text-gray-500" /> <span className="text-sm font-medium">AI Chat</span>
                      </Link>
                      <div className="border-t border-gray-100 my-1" />
                      <button onClick={() => { setProfileOpen(false); handleLogout(); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 transition text-red-600">
                        <LogOut className="w-4 h-4" /> <span className="text-sm font-medium">Esci</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/login" className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition" data-testid="nav-login">Accedi</Link>
              <Link to="/register" className="px-4 py-2 bg-[#10B981] hover:bg-[#059669] text-white text-sm font-semibold rounded-lg transition" data-testid="nav-register">Registrati</Link>
            </div>
          )}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-white/10" data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#1A1A2E] border-t border-white/10 px-4 py-3 space-y-1">
          {navLinks.map(link => (
            <Link key={link.path} to={link.path} className="flex items-center gap-3 py-2.5 text-slate-300 hover:text-white transition" onClick={() => setMobileMenuOpen(false)}>
              <link.icon className="w-5 h-5" /> <span className="text-sm font-medium">{link.label}</span>
            </Link>
          ))}
          {!user && (
            <div className="pt-2 border-t border-white/10 flex gap-2">
              <Link to="/login" className="flex-1 text-center py-2 text-sm font-medium text-slate-300 border border-slate-600 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Accedi</Link>
              <Link to="/register" className="flex-1 text-center py-2 text-sm font-semibold bg-[#10B981] text-white rounded-lg" onClick={() => setMobileMenuOpen(false)}>Registrati</Link>
            </div>
          )}
          {user && (
            <>
              <Link to="/wallet" className="flex items-center gap-3 py-2.5 text-blue-400 hover:text-white transition" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-wallet">
                <Wallet className="w-5 h-5" />
                <span className="text-sm font-medium">Wallet</span>
                {walletBalance !== null && <span className="ml-auto text-[11px] font-bold text-blue-300">€{walletBalance.toFixed(2).replace('.', ',')}</span>}
              </Link>
              <button onClick={() => { setMobileMenuOpen(false); setSupportOpen(true); }} className="flex items-center gap-3 py-2.5 text-[#10B981] hover:text-white transition w-full">
              <Headset className="w-5 h-5" /> <span className="text-sm font-medium">Supporto</span>
              {supportUnread > 0 && <span className="bg-red-500 text-white text-[9px] font-bold min-w-[16px] h-4 rounded-full flex items-center justify-center px-1 ml-auto">{supportUnread}</span>}
            </button>
            </>
          )}
        </div>
      )}

      {/* Support Panel */}
      <SupportPanel isOpen={supportOpen} onClose={() => { setSupportOpen(false); }} />
    </header>
  );
};

export default Navbar;
